import { ChangeEvent, useEffect, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";

interface Student {
    name: string;
    uid: string;
    status?: 'active' | 'suspended' | 'graduated';
}

const useStudentListView = (clickedLesson:string) => {
    const { readFirestoreWithQuery, readFirestoreOneDoc, updateFirestore, readFirestore } = useFirestore();
    const { getBranchId } = useGetBranchId();
    const [search, setSearch] = useState<string>('');
    const [lists, setLists] = useState<Student[]>([]);
    const [searchedLists, setSearchedLists] = useState<Student[]>([]);
    const [clickedItem, setClickedItem] = useState<string>("");
    const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
    const [isOpenStatusModal, setIsOpenStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState<'active' | 'suspended' | 'graduated'>('active');
    const [isLoading, setIsLoading] = useState(false);
    const [lessonList, setLessonList] = useState<any[]>([]);
    const [selectedLessonId, setSelectedLessonId] = useState<string>("");

    useEffect(() => {
        getStudentList();
        getLessonList();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (selectedLessonId || clickedLesson) {
            getAssignedStudentList();
        } else {
            getStudentList();
        }
    }, [selectedLessonId, clickedLesson])

    const getStudentList = async() => {
        setLists([]);
        setSearchedLists([]);
        const branch_id = await getBranchId();
        readFirestoreWithQuery("User/Auth/Student", {field:"branch_id", operator:"==", data:branch_id}).then(res => {
            const studentList = res.docs.map((doc:any) => ({...doc.data(), uid:doc.id}));
            studentList.sort((a: Student, b: Student) => a.name.localeCompare(b.name));
            setLists(studentList);
            setSearchedLists(studentList);
        })
    }

    const getAssignedStudentList = async() => {
        const branch_id = await getBranchId();
        setLists([]);
        setSearchedLists([]);
        const student_uid_list = await readFirestoreOneDoc(`Assign/${branch_id}/Lesson`, selectedLessonId || clickedLesson).then(res => {
            if(res.exists()){
                return res.data().students;
            }
            return [];
        })

        const studentPromises = student_uid_list.map((uid: string) => 
            readFirestoreOneDoc("User/Auth/Student", uid).then(res => {
                if(res.exists()){
                    return {...res.data(), uid:res.id};
                }
                return null;
            })
        );

        const students = await Promise.all(studentPromises);
        const validStudents = students.filter((student): student is Student => student !== null);
        validStudents.sort((a: Student, b: Student) => a.name.localeCompare(b.name));
        setLists(validStudents);
        setSearchedLists(validStudents);
    }

    useEffect(() => {
        const searchedItems = lists.filter((item) => item["name"].includes(search));
        searchedItems.sort((a: Student, b: Student) => a.name.localeCompare(b.name));
        setSearchedLists(searchedItems);
        // eslint-disable-next-line
    }, [search])

    const onRefreshClick = () => {
        if(selectedLessonId){
            getAssignedStudentList();
        }else{
            getStudentList();
        }
        getLessonList();
    }

    const onStudentClick = (item:string) => {
        setClickedItem(item);
    }

    const onChange = (e:ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }

    const onStudentSelect = (uid: string) => {
        setSelectedStudents(prev => {
            if (prev.includes(uid)) {
                return prev.filter(id => id !== uid);
            } else {
                return [...prev, uid];
            }
        });
    }

    const onUnAssignAcademy = async (uid:string) => {
        const branch_id = await getBranchId();
        const student_assigned_lesson_fetch_data = await readFirestoreWithQuery(`Assign/${branch_id}/Lesson`, {field:"students", operator:"array-contains", data:uid});
        if(!student_assigned_lesson_fetch_data.empty){
            const student_assigned_lesson_list = student_assigned_lesson_fetch_data.docs;
            for(const student_assigned_lesson of student_assigned_lesson_list){
                const data = student_assigned_lesson.data();
                const newdata = {
                    "students":data["students"].filter((stduent:string) => stduent !== uid)
                }
                
                await updateFirestore(`Assign/${branch_id}/Lesson`, student_assigned_lesson.id, newdata);
            }
        }
    }

    const onBulkStatusUpdate = async () => {
        setIsLoading(true);
        const updatePromises = selectedStudents.map(uid => {
                if(newStatus === "graduated"){
                    onUnAssignAcademy(uid);
                }
                updateFirestore("User/Auth/Student", uid, { status: newStatus })
            }
        );

        try {
            await Promise.all(updatePromises);
            if(selectedLessonId){
                getAssignedStudentList();
            }else{
                getStudentList();
            }
            setSelectedStudents([]);
            setIsOpenStatusModal(false);
        } catch (error) {
            console.error("Error updating student statuses:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const getLessonList = async() => {
        const branch_id = await getBranchId();
        const lesson_list = await readFirestore(`Lesson/Branch/${branch_id}`);
        const lesson_list_data = lesson_list.docs.map((doc:any) => ({...doc.data(), id:doc.id}));
        setLessonList(lesson_list_data);
    }

    const onLessonChange = (lessonId: string) => {
        setSelectedLessonId(lessonId);
    }

    const onStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setNewStatus(e.target.value as 'active' | 'suspended' | 'graduated');
    }

    return {
        searchedLists, 
        search, 
        onChange, 
        onStudentClick, 
        onRefreshClick, 
        clickedItem,
        selectedStudents,
        onStudentSelect,
        isOpenStatusModal,
        setIsOpenStatusModal,
        newStatus,
        onStatusChange,
        onBulkStatusUpdate,
        setSelectedStudents,
        isLoading,
        lessonList,
        onLessonChange,
        selectedLessonId
    }
}

export default useStudentListView;